import FileUploader, { FileUploaderRef } from 'devextreme-react/file-uploader';
import styles from './FileDropzone.module.scss';
import { useEffect, useRef, useState } from 'react';
import FileImageIcon from 'assets/icons/file-image-icon.svg?react';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import clsx from 'clsx';
import { useScopedTranslation } from '../../application/localization/useScopedTranslation.ts';
import { ActionText } from './ActionText.tsx';
import { IconButton } from './IconButton.tsx';

export type FileDropzoneProps = {
  supportedFileTypes?: string[];
  maxFileSize?: number;
  onFileSelected?: (file: File | null) => void;
  selectedFileProps?: File | null;
  selectedFileName?: string | null;
  errorText?: string;
  hideAttachFileText?: boolean;
};

export const FileDropzone = (props: FileDropzoneProps) => {
  const {
    supportedFileTypes = ['.docx, .pdf'],
    maxFileSize = 20 * 1024 * 1024,
    onFileSelected,
    selectedFileProps,
    selectedFileName,
    errorText = '',
    hideAttachFileText,
  } = props;
  const [onDropZoneEnter, setOnDropZoneEnter] = useState(false);
  const [FileUploadError, setFileUploadError] = useState<string>('');

  const [selectedFile, setSelectedFile] = useState<File | null>(
    selectedFileProps ?? null,
  );

  const uploaderRef = useRef<FileUploaderRef | null>(null);
  const i18n = useScopedTranslation('FileDropzone');

  useEffect(() => {
    setSelectedFile(selectedFileProps ?? null);
  }, [selectedFileProps]);

  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex === -1
      ? ''
      : filename.slice(lastDotIndex).toLowerCase();
  };

  const isValidFileType = (file: File): boolean => {
    const fileExtension = getFileExtension(file.name);
    return supportedFileTypes.includes(fileExtension);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setOnDropZoneEnter(false);
    uploaderRef.current?.instance()?.reset();
    onFileSelected?.(null);
  };

  const hasSelectedFile = !!selectedFile || !!selectedFileName;
  const displayedFileName = selectedFile?.name || selectedFileName || '';

  return (
    <>
      <div
        className={clsx(
          hasSelectedFile ? styles.columnContainer14 : styles.columnContainer4,
        )}
      >
        {!hideAttachFileText && (
          <div className={clsx(styles.greyText)}>{i18n.t('attach_file')}</div>
        )}

        {hasSelectedFile ? (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            id={'file'}
            className={styles.rowContainer4}
          >
            <div className={styles.fileName}>{displayedFileName}</div>

            <IconButton
              type={'destroy'}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                clearFile();
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        ) : (
          <div
            id="dropzone"
            className={clsx(
              onDropZoneEnter && styles.dropzoneEnter,
              styles.dropzone,
            )}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];

              if (file) {
                uploaderRef.current?.instance().option('value', [file]);
              }
            }}
          >
            <FileImageIcon />
            <div className={styles.rowContainer4}>
              <div className={styles.text}>{i18n.t('drag_and_drop')}</div>
              <div>
                <ActionText
                  onClickHandler={(event) => event.preventDefault()}
                  text={i18n.t('browse_file')}
                />
              </div>
            </div>
          </div>
        )}
        <div className={clsx(styles.dropzoneFooter)}>
          <div className={clsx(styles.rowContainer4, styles.italic)}>
            <div>{i18n.t('supported_formats')}</div>
            <div className={styles.supportedFormats}>
              {supportedFileTypes?.join(', ')}
            </div>
          </div>
          {(FileUploadError || errorText) && (
            <div className={styles.errorText}>
              {FileUploadError || errorText}
            </div>
          )}
        </div>
      </div>
      {!hasSelectedFile && (
        <FileUploader
          uploadMode={'useForm'}
          ref={uploaderRef}
          onDropZoneEnter={() => setOnDropZoneEnter(true)}
          onDropZoneLeave={() => setOnDropZoneEnter(false)}
          onUploadStarted={() => {
            setFileUploadError('');
            setOnDropZoneEnter(false);
          }}
          multiple={false}
          visible={false}
          showFileList={false}
          dialogTrigger="#dropzone"
          dropZone="#dropzone"
          accept={supportedFileTypes?.join(',')}
          onValueChanged={(e) => {
            setFileUploadError('');

            const files = e.value as File[];
            const file = files?.[0] ?? null;
            if (!file) return;

            if (!isValidFileType(file)) {
              setFileUploadError(i18n.t('incorrect_format_error'));
              return;
            }

            if (file.size > maxFileSize) {
              setFileUploadError(
                i18n.t('max_size_error', {
                  size: Math.round(maxFileSize / 1024 / 1024),
                }),
              );
              return;
            }

            setSelectedFile(file);
            onFileSelected?.(file);
          }}
        />
      )}
    </>
  );
};
